<template>
  <CModal
    class="variant-option-price-modal"
    title="Edit Variant Option Price"
    color="primary"
    size="lg"
    centered
    :show.sync="$store.state.menuV2.variantOptionPriceModalShow"
  >
    <template #header>
      <div class="d-flex justify-content-between align-items-center">
        <h5 class="modal-title">Edit Option Price for {{ option?.name }}</h5>
        <CBadge :color="option?.sold_out ? 'danger' : 'success'" class="badge-pill py-1 px-2 ml-2">
          {{ option?.sold_out ? 'Sold Out' : 'Available' }}
        </CBadge>
      </div>
      <CButtonClose @click="closeModal" class="text-white" />
    </template>

    <div v-if="option">
      <!-- Option Information Card -->
      <CRow class="mb-4">
        <CCol :md="usetoprice ? 6 : 12" class="mb-3 mb-md-0">
          <div class="price-display">
            <div class="price-label">Default Delivery Price</div>
            <div class="price-value" :class="getOptionPriceClass(option.price)">
              {{ parseFloat(option.price) | toCurrency }}
            </div>
          </div>
        </CCol>
        <CCol md="6" v-if="usetoprice">
          <div class="price-display">
            <div class="price-label">Default Takeout Price</div>
            <div class="price-value" :class="getOptionPriceClass(option.toPrice)">
              {{ parseFloat(option.toPrice) | toCurrency }}
            </div>
          </div>
        </CCol>
      </CRow>

      <!-- Variant Prices Section -->
      <h5 class="mb-1">Variant-Specific Prices</h5>
      <p class="text-muted small mb-2">Set custom prices for this option when used with specific variants. Leave empty to use the default price.</p>

      <CAlert color="light" v-if="!displayVariants.length">
        This menu doesn't have any variants to set custom prices for.
      </CAlert>

      <div v-else>
        <!-- Card-based Layout for Variants -->
        <div class="variant-cards">
          <CCard v-for="variant in displayVariants" :key="variant.id" class="variant-price-card mb-2 shadow-sm">
            <CCardHeader class="py-1 bg-light">
              <strong>{{ variant.name }}</strong>
              <small v-if="variant.description" class="text-muted d-block">{{ variant.description | truncate(60) }}</small>
            </CCardHeader>
            <CCardBody class="p-2">
              <CRow>
                <!-- Delivery Price Section -->
                <CCol :md="usetoprice ? 6 : 12" class="mb-1 mb-md-0">
                  <div class="price-section">
                    <div class="price-section-header d-flex justify-content-between align-items-center mb-1">
                      <span class="font-weight-bold">Delivery Price</span>
                      <!-- <CBadge color="info" class="default-price-badge">
                        Default: {{ parseFloat(option.price) | toCurrency }}
                      </CBadge> -->
                    </div>
                    <CInput
                      class="custom-price-input"
                      type="number"
                      step="0.01"
                      placeholder="Set custom price"
                      :value="variant.option_price"
                      @input="updateVariantOptionPrice(variant, 'option_price', $event)"
                    >
                      <template #prepend-content><CIcon name="cil-euro"/></template>
                    </CInput>
                  </div>
                </CCol>

                <!-- Takeout Price Section -->
                <CCol md="6" v-if="usetoprice">
                  <div class="price-section">
                    <div class="price-section-header d-flex justify-content-between align-items-center mb-1">
                      <span class="font-weight-bold">Takeout Price</span>
                      <!-- <CBadge color="info" class="default-price-badge">
                        Default: {{ parseFloat(option.toPrice) | toCurrency }}
                      </CBadge> -->
                    </div>
                    <CInput
                      class="custom-price-input"
                      type="number"
                      step="0.01"
                      placeholder="Set custom price"
                      :value="variant.option_toPrice"
                      @input="updateVariantOptionPrice(variant, 'option_toPrice', $event)"
                    >
                      <template #prepend-content><CIcon name="cil-euro"/></template>
                    </CInput>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </div>
      </div>
    </div>

    <CElementCover :opacity="0.4" v-show="isSubmitting"/>

    <template #footer class="p-1">
      <CButton @click="closeModal" color="secondary">Close</CButton>
      <CButton color="primary" @click="saveVariantOptionPrices" :disabled="isSubmitting">
        <CSpinner v-if="isSubmitting" size="sm" color="light" class="mr-1" />
        {{ isSubmitting ? 'Saving...' : 'Save Changes' }}
      </CButton>
    </template>
  </CModal>
</template>

<script>
export default {
  name: "VariantOptionPriceModal",

  props: {
    option: { type: Object|null, required: true },
    variants: { type: Array, default: () => [] },
    usetoprice: { type: Boolean, default: false }
  },

  data() {
    return {
      isSubmitting: false,
      changedVariants: new Set(),
    }
  },

  computed: {
    restaurantId() { return this.$route.params.restaurant_id },
    menuId() { return this.$route.params.id },
    displayVariants: {
      get() { return this.variants.map(variant => variant) },
      set(newVal) { this.variants = newVal }
    }
  },

  watch: {
    '$store.state.menuV2.variantOptionPriceModalShow': {
      handler(newVal) {
        if (newVal) {
          this.changedVariants.clear();
        }
      },
      immediate: true
    }
  },

  methods: {
    closeModal() {
      this.$store.state.menuV2.variantOptionPriceModalShow = false;
    },

    getOptionPriceClass(price) {
      const numPrice = parseFloat(price);
      return numPrice > 0 ? 'text-success' : numPrice < 0 ? 'text-danger' : 'text-muted';
    },

    updateVariantOptionPrice(variant, type, value) {
      variant[type] = value;

      this.changedVariants.add(variant.id);
    },

    async saveVariantOptionPrices() {
      this.isSubmitting = true;
      let success = true;

      try {
        for (const variantId of this.changedVariants) {
          const variant = this.displayVariants.find(p => p.id === variantId);
          const url = this.$backend.MENU_V2.UPDATE_OPTION_PRICE(this.restaurantId, this.menuId, variantId);

          try {
            await this.$axios.post(url, {
              option_id: this.option.id,
              price: variant?.option_price,
              toPrice: variant?.option_toPrice
            });
          } catch (error) { success = false; }
        }

        if (success) {
          this.$toast.success('Variant option prices updated successfully');
          this.$emit('variant-option-price-updated', this.displayVariants);
          this.closeModal();
        } else {
          this.$toast.warning('Some prices could not be updated. Please try again.');
        }
      } finally {
        this.isSubmitting = false;
        this.changedVariants.clear();
      }
    },
  }
}
</script>

<style scoped>
.variant-cards {
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 5px;
  width: 100%;
}

.price-display {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px 12px;
  height: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.price-label {
  font-size: 0.9rem;
  margin-bottom: 3px;
  font-weight: 500;
}

.price-value {
  font-size: 1.2rem;
  font-weight: bold;
}

.variant-price-card {
  transition: all 0.2s ease;
  border-left: 3px solid #20a8d8;
}

.variant-price-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
}

.price-section {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
}

.default-price-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.custom-price-input {
  margin-top: 4px;
  margin-bottom: 0;
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-muted {
  color: #6c757d !important;
}
</style>

<style>
.custom-price-input .form-control {
  font-weight: 600;
  font-size: 1.15rem;
  color: #321fdb !important;
}
</style>
